import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react'
import { Button } from './Button'

interface PaginationProps {
  currentPage: number
  totalPages: number
  totalItems: number
  itemsPerPage: number
  onPageChange: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
  showPageSizeSelector?: boolean
  pageSizeOptions?: number[]
  className?: string
}

export const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onPageSizeChange,
  showPageSizeSelector = true,
  pageSizeOptions = [10, 25, 50, 100],
  className = ''
}: PaginationProps) => {
  // Generate page numbers to display
  const getPageNumbers = (isMobile: boolean = false) => {
    const pages: (number | 'ellipsis')[] = []
    const maxVisible = isMobile ? 3 : 7

    if (totalPages <= maxVisible) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      if (isMobile) {
        // Mobile: Show only current page with ellipsis
        pages.push(1)
        if (currentPage > 2) {
          pages.push('ellipsis')
        }
        if (currentPage !== 1 && currentPage !== totalPages) {
          pages.push(currentPage)
        }
        if (currentPage < totalPages - 1) {
          pages.push('ellipsis')
        }
        pages.push(totalPages)
      } else {
        // Desktop: Show more pages
        // Always show first page
        pages.push(1)

        if (currentPage <= 4) {
          // Show first few pages
          for (let i = 2; i <= 5; i++) {
            pages.push(i)
          }
          pages.push('ellipsis')
          pages.push(totalPages)
        } else if (currentPage >= totalPages - 3) {
          // Show last few pages
          pages.push('ellipsis')
          for (let i = totalPages - 4; i <= totalPages; i++) {
            pages.push(i)
          }
        } else {
          // Show pages around current page
          pages.push('ellipsis')
          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
            pages.push(i)
          }
          pages.push('ellipsis')
          pages.push(totalPages)
        }
      }
    }

    return pages
  }

  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalItems)

  if (totalPages <= 1 && !showPageSizeSelector) {
    return null
  }

  return (
    <div className={`flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between ${className}`}>
      {/* Results info */}
      <div className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 text-center sm:text-left">
        Showing{' '}
        <span className="font-medium">{startItem}</span>
        {' to '}
        <span className="font-medium">{endItem}</span>
        {' of '}
        <span className="font-medium">{totalItems}</span>
        {' results'}
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6">
        {/* Page size selector */}
        {showPageSizeSelector && onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">Show:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="px-2 sm:px-3 py-1 text-xs sm:text-sm border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Pagination controls */}
        {totalPages > 1 && (
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Previous button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex items-center gap-1 px-2 sm:px-3"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Previous</span>
            </Button>

            {/* Page numbers - Desktop */}
            <div className="hidden sm:flex items-center gap-1">
              {getPageNumbers(false).map((page, index) => (
                <div key={index}>
                  {page === 'ellipsis' ? (
                    <div className="flex items-center justify-center w-8 h-8">
                      <MoreHorizontal className="h-4 w-4 text-gray-400" />
                    </div>
                  ) : (
                    <Button
                      variant={page === currentPage ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => onPageChange(page as number)}
                      className={`w-8 h-8 p-0 ${
                        page === currentPage
                          ? 'bg-orange-500 text-white hover:bg-orange-600'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {page}
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Page numbers - Mobile */}
            <div className="flex sm:hidden items-center gap-1">
              {getPageNumbers(true).map((page, index) => (
                <div key={index}>
                  {page === 'ellipsis' ? (
                    <div className="flex items-center justify-center w-7 h-7">
                      <MoreHorizontal className="h-3 w-3 text-gray-400" />
                    </div>
                  ) : (
                    <Button
                      variant={page === currentPage ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => onPageChange(page as number)}
                      className={`w-7 h-7 p-0 text-xs ${
                        page === currentPage
                          ? 'bg-orange-500 text-white hover:bg-orange-600'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {page}
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Next button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1 px-2 sm:px-3"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}